import { useEffect, useState } from "react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DATA_OPTIONS = ["Таблицы", "CRM", "1С", "Чаты", "Сайт", "Всё сразу"];

const Field = ({ label, hint, children, error, htmlFor }) => (
  <div>
    {label && (
      <label htmlFor={htmlFor} className="mb-2 block text-base font-medium">
        {label}
      </label>
    )}
    {children}
    {hint && <p className="mt-2 text-sm text-muted">{hint}</p>}
    {error && (
      <p role="alert" className="mt-2 text-sm text-danger" data-testid="field-error">
        {error}
      </p>
    )}
  </div>
);

// Мягкая проверка «это вообще похоже на способ связи» — email, телефон или
// telegram-ник (с @ или без). Не блокирует отправку, только подсказывает.
const looksLikeContact = (v) => {
  const s = v.trim();
  if (!s) return true;
  const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phone = /^\+?[\d\s().-]{10,20}$/;
  const tgHandle = /^@?[a-zA-Z][a-zA-Z0-9_]{4,31}$/;
  return email.test(s) || phone.test(s) || tgHandle.test(s);
};

export const LeadForm = ({ initialProcess = "", initialHours = "", initialRate = "" }) => {
  const [form, setForm] = useState({
    process: "",
    people_count: "",
    hours_per_week: "",
    contact: "",
  });
  const [locations, setLocations] = useState([]);
  const [rate, setRate] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [contactTouched, setContactTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);

  // Подставляем выбранную в 01 «подбор» нужду в поле «что за процесс?», а вместе
  // с ней — часы и ставку, если клиент их там уже вводил. Иначе одно и то же
  // число пришлось бы вводить дважды. Срабатывает только на новый выбор в
  // подборе (initialProcess меняется) — руками начатый ввод не затирается.
  useEffect(() => {
    if (!initialProcess) return;
    setForm((f) => ({
      ...f,
      process: initialProcess,
      hours_per_week: initialHours || f.hours_per_week,
    }));
    if (initialRate) setRate(initialRate);
  }, [initialProcess, initialHours, initialRate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleLoc = (opt) =>
    setLocations((prev) =>
      prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]
    );

  const validate = () => {
    const e = {};
    if (!form.process.trim()) e.process = "Опиши процесс хотя бы парой фраз.";
    if (!form.contact.trim())
      e.contact = "Оставь Telegram или почту, чтобы я мог прислать разбор.";
    if (!consent)
      e.consent = "Отметь согласие на обработку данных, без него я не могу принять заявку.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          process: form.process.trim(),
          people_count: form.people_count ? Number(form.people_count) : null,
          hours_per_week: form.hours_per_week ? Number(form.hours_per_week) : null,
          rate_per_hour: rate ? Number(rate) : null,
          data_locations: locations,
          contact: form.contact.trim(),
          consent,
          website: honeypot,
        }),
      });
      // 422 — ошибка ввода (поле не прошло серверную проверку), это вина
      // формы, не канала доставки. 502/503/сеть — Telegram недоступен, это
      // не вина посетителя, тут нужна отдельная честная ветка (см. ниже),
      // не тихий "не отправилось" под кнопкой.
      if (res.status === 422) {
        setErrors({ submit: "Одно из полей не прошло проверку. Проверь форму и отправь ещё раз." });
        return;
      }
      if (!res.ok) {
        setDeliveryFailed(true);
        return;
      }
      setDone(true);
    } catch {
      setDeliveryFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (deliveryFailed) {
    return (
      <Reveal>
        <div className="surface p-8 sm:p-12" data-testid="form-delivery-failed">
          <p className="font-mono text-sm text-danger">Не отправилось</p>
          <p role="alert" className="mt-4 max-w-[560px] text-2xl font-semibold sm:text-3xl">
            Заявка не дошла. Подвела доставка, а не форма.
          </p>
          <p className="mt-4 max-w-[560px] text-base text-muted">
            Всё введённое осталось в форме. Можно отправить ещё раз или написать мне напрямую в Telegram и коротко
            пересказать, что было в форме.
          </p>
          <a
            href="https://t.me/d_evoluer"
            target="_blank"
            rel="noopener noreferrer"
            className="btn mt-8"
            data-testid="delivery-failed-telegram"
          >
            Написать в Telegram
          </a>
          <button
            type="button"
            onClick={() => setDeliveryFailed(false)}
            className="mt-6 block text-sm text-muted underline underline-offset-4 transition-colors duration-300 ease-smooth hover:text-white"
            data-testid="delivery-failed-retry"
          >
            Попробовать ещё раз
          </button>
        </div>
      </Reveal>
    );
  }

  if (done) {
    return (
      <Reveal>
        <div className="surface p-8 sm:p-12" data-testid="form-success">
          <p className="font-mono text-sm text-accent">Получено</p>
          <p role="status" className="mt-4 max-w-[560px] text-2xl font-semibold sm:text-3xl">
            Получил. Пришлю разбор в течение 2 рабочих дней.
          </p>
          <ol className="mt-8 max-w-[560px] list-inside list-decimal space-y-3 text-base text-muted">
            <li>Смотрю процесс, который ты описал.</li>
            <li>Считаю, где утекают часы и деньги.</li>
            <li>Пишу, что можно закрыть и что для этого нужно.</li>
            <li>Присылаю разбор с вилкой цены на контакт, который ты оставил.</li>
          </ol>
          <p className="mt-8 max-w-[560px] text-sm text-muted">
            Есть вопрос по пути или срочно, пиши в Telegram:{" "}
            <a
              href="https://t.me/d_evoluer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white underline underline-offset-4"
              data-testid="success-telegram"
            >
              @d_evoluer
            </a>
            .
          </p>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal>
      <div className="surface p-6 sm:p-10">
        <form onSubmit={submit} className="space-y-8" noValidate data-testid="lead-form">
          <Field
            label="Что за процесс?"
            htmlFor="f-process"
            hint="Своими словами, как есть. Красиво формулировать не нужно."
            error={errors.process}
          >
            <textarea
              id="f-process"
              className="fld"
              value={form.process}
              onChange={set("process")}
              placeholder="Например: заявки приходят в чаты, я переношу их в таблицу и вручную собираю отчёт"
              aria-invalid={!!errors.process}
              data-testid="input-process"
            />
          </Field>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <Field label="Сколько человек в нём заняты?" htmlFor="f-people" hint="Необязательно." error={errors.people_count}>
              <input
                id="f-people"
                type="number"
                min="1"
                placeholder="например, 2"
                className="fld"
                value={form.people_count}
                onChange={set("people_count")}
                data-testid="input-people"
              />
            </Field>
            <Field
              label="Сколько часов в неделю он съедает?"
              htmlFor="f-hours"
              hint="Прикинь честно, точность не нужна. Необязательно."
              error={errors.hours_per_week}
            >
              <input
                id="f-hours"
                type="number"
                min="1"
                placeholder="например, 5"
                className="fld"
                value={form.hours_per_week}
                onChange={set("hours_per_week")}
                data-testid="input-hours"
              />
            </Field>
          </div>

          <fieldset>
            <legend className="mb-2 block text-base font-medium">Где живут данные?</legend>
            <div className="flex flex-wrap gap-3">
              {DATA_OPTIONS.map((opt) => {
                const on = locations.includes(opt);
                return (
                  <label key={opt} className="chk" data-on={on} data-testid={`loc-${opt}`}>
                    <input type="checkbox" className="sr-only" checked={on} onChange={() => toggleLoc(opt)} />
                    <span className="box" aria-hidden="true" />
                    {opt}
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-sm text-muted">Можно отметить несколько. Необязательно.</p>
          </fieldset>

          {/* Honeypot: невидим человеку, боты часто заполняют поля вслепую.
              Заполнено, значит бэкенд молча отбивает заявку. */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            className="sr-only"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            data-testid="input-honeypot"
          />

          <Field
            label="Как с тобой связаться?"
            htmlFor="f-contact"
            hint={
              contactTouched && form.contact.trim() && !looksLikeContact(form.contact)
                ? "Не похоже на почту, Telegram или телефон. Если это опечатка, поправь."
                : "Telegram или почта."
            }
            error={errors.contact}
          >
            <input
              id="f-contact"
              type="text"
              placeholder="@username или name@example.ru"
              className="fld"
              value={form.contact}
              onChange={set("contact")}
              onBlur={() => setContactTouched(true)}
              aria-invalid={!!errors.contact}
              data-testid="input-contact"
            />
          </Field>

          {/* Согласие на обработку ПДн: обязательное, не предотмеченное (152-ФЗ). */}
          <Field error={errors.consent}>
            <label className="chk-row" data-on={consent} data-testid="input-consent">
              <input
                type="checkbox"
                className="sr-only"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span className="box" aria-hidden="true" />
              <span className="text-sm text-muted">
                Соглашаюсь на{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline underline-offset-4"
                  data-testid="consent-privacy-link"
                >
                  обработку персональных данных
                </a>
              </span>
            </label>
          </Field>

          {errors.submit && (
            <p role="alert" className="text-sm text-danger" data-testid="submit-error">
              {errors.submit}
            </p>
          )}

          <button type="submit" className="btn" disabled={loading} data-testid="submit-button">
            {loading ? "Отправляю…" : "Получить письменный разбор"}
          </button>

          <p className="max-w-[560px] text-sm text-muted">
            Пришлю разбор в течение 2 рабочих дней: что за процесс, где потери, что можно закрыть, какая вилка цены.
            Бесплатно и без обязательств. Если задача не под меня, так и напишу и объясню почему.
          </p>
        </form>
      </div>
    </Reveal>
  );
};
